import React, { useMemo, useState } from "react";
import {
  Users,
  TrendingUp,
  Receipt,
  Copy,
  Check,
  AlertCircle,
  Clock,
  CheckCircle2,
  BadgeCheck,
} from "lucide-react";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import {
  formatKES,
  formatMonth,
  formatDate,
  toLocalDate,
} from "../utils/format";

const LONG_MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** "2026-05-17" → "17th May 2026". Falls back to formatDate. */
function formatLongDate(iso) {
  if (!iso) return "—";
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return formatDate(iso);
  const [, y, mo, d] = m;
  return `${ordinal(Number(d))} ${LONG_MONTHS[Number(mo) - 1]} ${y}`;
}
import { calculateBill, FLAT_RATE, PAYBILL_NUMBER } from "../utils/billing";
import StatCard from "../components/StatCard";

export default function Billing() {
  const { user } = useAuth();
  const { tenants, properties, billing } = useData();
  const [copied, setCopied] = useState(null); // "paybill" | "account" | null
  const activeCount = tenants.filter((t) => t.status === "ACTIVE").length;
  const peak = billing.billingPeakTenants;
  const bill = calculateBill(peak);

  // Account format mirrors the mobile app: 068001#<userAccountRef>.
  const acctRef = (user?.accountNumber || "").trim();
  const fullAccount = acctRef ? `068001#${acctRef}` : "";

  const copy = (key, text) => {
    if (!text) return;
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  // Nothing to bill yet? A fresh account with no properties or
  // tenants doesn't need the due-date / invoice / cost-breakdown
  // clutter — just a quick "what Paysafi costs" summary.
  const emptyAccount =
    properties.length === 0 && tenants.length === 0 && peak === 0;

  if (billing.waived) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
            Billing
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Your account has been marked as waived by the Paysafi team.
          </p>
        </div>

        <div className="card border-green-200 bg-green-50 p-8 dark:border-green-800 dark:bg-green-950">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              <BadgeCheck className="h-6 w-6" />
            </span>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-green-800 dark:text-green-300">
                Billing waived by Paysafi
              </h3>
              <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                You owe <b>KES 0</b>. You can keep using Paysafi normally; all
                features remain available.
              </p>
              {billing.waivedAt && (
                <p className="mt-3 text-xs text-green-700 dark:text-green-400">
                  Waived on <b>{formatDate(billing.waivedAt)}</b>
                  {billing.waivedBy ? (
                    <>
                      {" "}
                      by <b>{billing.waivedBy}</b>
                    </>
                  ) : null}
                  .
                </p>
              )}
              {billing.waivedReason && (
                <p className="mt-2 rounded-md bg-white/60 px-3 py-2 text-xs italic text-green-800 dark:bg-black/20 dark:text-green-300">
                  "{billing.waivedReason}"
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
          Questions about your account? Contact{" "}
          <a
            href="mailto:botibongasolutions@gmail.com"
            className="font-medium text-indigo-600 hover:underline"
          >
            botibongasolutions@gmail.com
          </a>
          .
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Billing
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {emptyAccount
            ? "Paysafi is free until you add your first tenant. You'll be billed " +
              `${formatKES(FLAT_RATE)} per tenant per month based on your peak ` +
              "count during each billing cycle."
            : "Your estimated bill for this cycle. Billing is based on the " +
              "highest number of active tenants between the start and end of " +
              "the cycle."}
        </p>
      </div>

      {!emptyAccount && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            label="Active tenants"
            value={activeCount}
            icon={Users}
            hint="Currently occupying"
          />
          <StatCard
            label="Peak this cycle"
            value={peak}
            icon={TrendingUp}
            hint="Highest during billing cycle"
            tone={peak > activeCount ? "warning" : "default"}
          />
          <StatCard
            label="Estimated cost"
            value={formatKES(bill.total)}
            icon={Receipt}
            hint={`${peak} x ${formatKES(FLAT_RATE)}`}
            tone="success"
          />
        </div>
      )}

      {!emptyAccount && (
        <InvoiceStatus billing={billing} bill={bill} peak={peak} />
      )}

      {/* M-Pesa payment info — always visible so landlords know how to pay. */}
      <div className="card p-6">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
          Pay via M-Pesa
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Use these details exactly as shown — the account number is what
          links the payment to your Paysafi account.
        </p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <PayRow
            label="Paybill number"
            value={PAYBILL_NUMBER}
            copyKey="paybill"
            copied={copied}
            onCopy={() => copy("paybill", PAYBILL_NUMBER)}
          />
          <PayRow
            label="Account number"
            value={fullAccount || "Not set"}
            copyKey="account"
            copied={copied}
            onCopy={() => copy("account", fullAccount)}
            disabled={!fullAccount}
            accent
          />
        </div>
        {fullAccount && (
          <div className="mt-4 rounded-lg bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 px-4 py-2.5 text-xs text-amber-800 dark:text-amber-300">
            <strong>Important:</strong> Enter the account number exactly as{" "}
            <span className="font-mono font-bold">{fullAccount}</span> when
            paying via M-Pesa Paybill to ensure automatic matching.
          </div>
        )}
      </div>

      {!emptyAccount && (
        <div className="card p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Cost breakdown
          </h3>
          <div className="mt-4 space-y-3">
            <Row label="Peak active tenants" value={peak} />
            <Row label="Price per tenant" value={formatKES(FLAT_RATE)} />
            <Row label="Subtotal" value={formatKES(bill.subtotal)} mono />
            <div className="flex items-center justify-between border-t border-gray-100 dark:border-gray-700 pt-3">
              <span className="text-base font-semibold text-gray-900 dark:text-white">
                Total
              </span>
              <span className="text-2xl font-bold text-indigo-600">
                {formatKES(bill.total)}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg bg-gray-50 dark:bg-gray-900 px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
        <strong className="font-semibold text-gray-700 dark:text-gray-300">
          How billing works:
        </strong>{" "}
        Paysafi costs {formatKES(FLAT_RATE)} per tenant per month, regardless
        of how many tenants you have. Your first invoice is due one month
        after you signed up, and every month thereafter on the same day. The
        peak number of active tenants during each cycle is what we bill for
        — resets at the start of every new cycle so vacating tenants before
        the cycle end doesn't avoid the charge.
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-500 dark:text-gray-400">{label}</span>
      <span
        className={`font-medium text-gray-900 dark:text-white ${mono ? "font-mono" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function PayRow({ label, value, copyKey, copied, onCopy, disabled, accent }) {
  const wrapper = accent
    ? "rounded-xl border border-indigo-200 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-950 p-5"
    : "rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-5";
  const labelClass = accent
    ? "text-xs font-medium uppercase tracking-wide text-indigo-600 dark:text-indigo-400"
    : "text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400";
  const valueClass = accent
    ? "min-w-0 break-all font-mono text-2xl font-bold tracking-wider text-indigo-700 dark:text-indigo-300 sm:text-3xl"
    : "text-3xl font-bold tracking-wider text-gray-900 dark:text-white";

  return (
    <div className={wrapper}>
      <div className={labelClass}>{label}</div>
      <div className="mt-2 flex min-w-0 flex-wrap items-center gap-2">
        <span className={valueClass}>{value}</span>
        <button
          onClick={onCopy}
          disabled={disabled}
          className="rounded-lg border border-gray-200 bg-white p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
          aria-label={`Copy ${label.toLowerCase()}`}
        >
          {copied === copyKey ? (
            <Check className="h-4 w-4 text-green-600" />
          ) : (
            <Copy className="h-4 w-4" />
          )}
        </button>
      </div>
      {copied === copyKey && (
        <div className="mt-2 text-xs font-medium text-green-700 dark:text-green-400">
          Copied to clipboard
        </div>
      )}
    </div>
  );
}

function InvoiceStatus({ billing, bill, peak }) {
  // Invoice due on the cycle end date the backend returns. Fall back
  // to a reasonable guess based on billingMonth if the new field
  // isn't there (old client / stale cache).
  const fallbackMonth = billing.billingMonth;
  const computedFallback = useMemo(() => {
    if (!fallbackMonth) return null;
    const [y, m] = fallbackMonth.split("-").map(Number);
    const next = m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, "0")}`;
    return `${next}-01`;
  }, [fallbackMonth]);
  const dueDate = billing.billingDueDate || computedFallback;
  const cycleStart = billing.billingCycleStart || null;

  const todayStr = toLocalDate();
  const { statusLabel, statusTone, StatusIcon, body, chipTone } = useMemo(() => {
    const today = new Date(todayStr);
    const due = dueDate ? new Date(dueDate) : null;
    const msPerDay = 24 * 60 * 60 * 1000;
    const daysToDue = due ? Math.round((due - today) / msPerDay) : null;

    if (due && daysToDue !== null && daysToDue < 0) {
      return {
        statusLabel: "Overdue",
        statusTone:
          "border-red-200 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-300",
        chipTone: "bg-red-200 text-red-900",
        StatusIcon: AlertCircle,
        body: `This invoice was due on ${formatLongDate(dueDate)} and is now overdue. Please pay immediately to avoid service interruption.`,
      };
    }
    if (due && daysToDue !== null && daysToDue <= 3) {
      return {
        statusLabel: daysToDue === 0 ? "Due today" : `Due in ${daysToDue} day${daysToDue === 1 ? "" : "s"}`,
        statusTone:
          "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300",
        chipTone: "bg-amber-200 text-amber-900",
        StatusIcon: Clock,
        body: `This invoice is due on ${formatLongDate(dueDate)}. Settle it in the next few days to stay current.`,
      };
    }
    return {
      statusLabel: "Upcoming",
      statusTone:
        "border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-300",
      chipTone: "bg-green-200 text-green-900",
      StatusIcon: CheckCircle2,
      body: due
        ? `This invoice is due on ${formatLongDate(dueDate)}. Pay anytime before that date.`
        : "Your first invoice will become available one month after you signed up.",
    };
  }, [dueDate, todayStr]);

  return (
    <div className={`rounded-2xl border p-5 ${statusTone}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <StatusIcon className="mt-0.5 h-5 w-5 flex-none" />
          <div>
            <h3 className="text-base font-semibold">
              {cycleStart
                ? `Current billing cycle · starts ${formatLongDate(cycleStart)}`
                : `Invoice for ${formatMonth(billing.billingMonth)}`}
            </h3>
            <p className="mt-1 text-sm opacity-80">{body}</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs font-medium uppercase tracking-wide opacity-70">
            Amount due
          </div>
          <div className="mt-1 text-2xl font-bold">
            {formatKES(bill.total)}
          </div>
          <div className="mt-0.5 text-xs opacity-70">
            {peak} tenant{peak === 1 ? "" : "s"} x {formatKES(FLAT_RATE)}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-current/10 pt-4">
        <span
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${chipTone}`}
        >
          {statusLabel}
        </span>
        {dueDate && (
          <span className="text-xs opacity-70">Due: {formatDate(dueDate)}</span>
        )}
        {cycleStart && (
          <span className="text-xs opacity-70">
            Cycle start: {formatDate(cycleStart)}
          </span>
        )}
      </div>
    </div>
  );
}
